import { createBrowserRouter, Navigate, Link, isRouteErrorResponse, useRouteError } from "react-router-dom";
import { DemoLayout } from "./DemoLayout";
import { DemoEntry } from "./DemoEntry";
import { DemoDashboard } from "./DemoDashboard";
import { SessionView } from "./SessionView";
import { AddClientFlow } from "./AddClientFlow";
import { CheckinHistory } from "./CheckinHistory";
import { DemoAgreements } from "./DemoAgreements";
import { DemoClientProfile } from "./DemoClientProfile";
import { DemoSessionDetail } from "./DemoSessionDetail";
import { DemoSoundboard } from "./DemoSoundboard";
import { DemoScripts } from "./DemoScripts";
import { DemoSettings } from "./DemoSettings";
import { DemoCalendar } from "./DemoCalendar";
import { LiveEntry } from "./LiveEntry";
import { LiveJoin } from "./LiveJoin";
import { LIVE } from "../demo/live/live";
import type { RouteObject } from "react-router-dom";

/** Lightweight placeholder until the screen lands (Phase 3+). */
function Placeholder({ title }: { title: string }) {
  return (
    <main className="mx-auto flex min-h-[60dvh] max-w-2xl flex-col items-center justify-center px-6 text-center">
      <h1 className="font-heading text-3xl font-medium text-[var(--color-text-primary)]">{title}</h1>
      <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--color-text-ghost)]">
        Under construction
      </p>
    </main>
  );
}

/**
 * Branded error boundary — replaces React Router's built-in developer error page
 * ("💿 Hey developer 👋…"), which is a dead end with no nav and no way back.
 * Static + self-contained (no workspace imports) so the isolation guard stays green.
 */
function DemoErrorPage() {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  return (
    <main className="mx-auto flex min-h-dvh max-w-2xl flex-col items-center justify-center px-6 text-center">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--color-gold-soft)]">
        Psy180 · Interactive demo
      </p>
      <h1 className="mt-3 font-heading text-3xl font-medium text-[var(--color-text-primary)]">
        {notFound ? "That page isn't part of the demo" : "Something went sideways"}
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-[var(--color-text-muted)]">
        {notFound
          ? "The link may be mistyped — everything in the demo is reachable from the workspace."
          : "The demo hit an unexpected error. Nothing is saved here, so it's safe to jump back in."}
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link
          to="/dashboard"
          className="rounded-md border-2 border-[var(--color-gold)] bg-[var(--color-gold)] px-6 py-3 font-sans text-sm font-semibold uppercase tracking-wide text-[var(--color-bg-deep)]"
        >
          Enter the demo workspace
        </Link>
        <Link
          to="/"
          className="rounded-md border-2 border-[var(--color-border-strong)] px-6 py-3 font-sans text-sm font-semibold uppercase tracking-wide text-[var(--color-text-primary)]"
        >
          Demo home
        </Link>
      </div>
    </main>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <DemoLayout />,
    errorElement: <DemoErrorPage />,
    children: [
      { index: true, element: <DemoEntry /> },
      { path: "dashboard", element: <DemoDashboard /> },
      { path: "session", element: <SessionView /> },
      { path: "add-client", element: <AddClientFlow /> },
      { path: "history", element: <CheckinHistory /> },
      { path: "agreements", element: <DemoAgreements /> },
      { path: "client/:id", element: <DemoClientProfile /> },
      { path: "client/:id/session/:sessionId", element: <DemoSessionDetail /> },
      // Authed tabs mirroring the real app nav. Clients reuses the roster for now;
      // the rest land as the full-app mirror is built tab by tab.
      { path: "clients", element: <DemoDashboard /> },
      { path: "calendar", element: <DemoCalendar /> },
      { path: "profile", element: <Navigate to="/settings" replace /> },
      { path: "soundboard", element: <DemoSoundboard /> },
      { path: "scripts", element: <DemoScripts /> },
      { path: "settings", element: <DemoSettings /> },
      // Co-driven demo entry points — LIVE builds only (kept out of the static site).
      ...(LIVE
        ? ([
            { path: "live", element: <LiveEntry /> },
            { path: "live/:code", element: <LiveJoin /> },
          ] satisfies RouteObject[])
        : []),
    ],
  },
]);
